import { useMemo } from "react";

import { isNumber } from "src/utils";

// Inner imports
import { RawChartItem, ChartItem } from "../types";
import {
  getDataFilteredWithRange,
  getExtendedDataWithDashboardDateRange,
} from "../utils";

type Props = {
  data: RawChartItem[];
  dashboardDateRange?: DashboardDateRange.Data;
};

type ReturnType = {
  data: ChartItem[];
  forecastStartDate: number;
};

export const usePreparedLineChartData = ({
  data,
  dashboardDateRange,
}: Props): ReturnType => {
  const { data: formattedData, forecastData } = useMemo(() => {
    const [actualData, forecastData] = [
      new Set<ChartItem>(),
      new Set<ChartItem>(),
    ];

    for (const { date, ...rest } of data) {
      try {
        const time = Date.parse(date);

        if (!isNumber(time)) continue;

        const formattedItem: ChartItem = { ...rest, time };

        switch (true) {
          case rest.isForecasted:
            forecastData.add(formattedItem);

            break;
          default:
            actualData.add(formattedItem);

            break;
        }
      } catch (err) {
        console.error(err);
      }
    }

    const [sortedActualData, sortedForecastData] = [
      [...actualData].sort((a, b) => a.time - b.time),
      [...forecastData].sort((a, b) => a.time - b.time),
    ];

    if (sortedForecastData.length) {
      const lastActualDataItem = sortedActualData[sortedActualData.length - 1];

      if (lastActualDataItem)
        sortedForecastData.unshift({
          ...lastActualDataItem,
          time: lastActualDataItem.time + 1,
          isForecasted: true,
        });
    }

    return {
      data: [...sortedActualData, ...sortedForecastData],
      actualData: sortedActualData,
      forecastData: sortedForecastData,
    };
  }, [data]);

  const forecastStartDate = useMemo<number>(() => {
    let forecastStartDate = 0;

    const firstForecastItem = forecastData[0];

    if (firstForecastItem) forecastStartDate = firstForecastItem.time;

    return forecastStartDate;
  }, [forecastData]);

  const extendedData = useMemo<ChartItem[]>(() => {
    if (dashboardDateRange)
      return getExtendedDataWithDashboardDateRange({
        data: formattedData,
        dashboardDateRange,
      });

    return formattedData;
  }, [dashboardDateRange, formattedData]);

  const filteredData = useMemo<ChartItem[]>(() => {
    if (dashboardDateRange)
      return getDataFilteredWithRange({
        data: extendedData,
        endDate: dashboardDateRange.endDate,
        startDate: dashboardDateRange.startDate,
      });

    return extendedData;
  }, [dashboardDateRange, extendedData]);

  return { forecastStartDate, data: filteredData };
};
